import { useLocale, useTranslations } from "next-intl"

import { Cents, Text } from "@/components/ui"
import type { Locale } from "@/i18n/locales"
import { dateFormatter } from "@/lib/helpers/date-format"
import { addMoney, formatMoneyWithCode, isPositiveMoney, money } from "@/lib/money"
import type { InstallmentView } from "@/lib/money/price-view"

interface QuincenaSplitProps {
  plan: InstallmentView
  disclaimer?: string
  className?: string
}

export const QuincenaSplit = ({ plan, disclaimer, className }: QuincenaSplitProps) => {
  const t = useTranslations("marketplace.bnpl")
  const locale = useLocale() as Locale
  const total = plan.downPayment ? addMoney(plan.total, plan.downPayment) : plan.total
  const rows = plan.financingMode === "SECURED" ? securedRows(plan, t, locale) : regularRows(plan, t, locale)

  return (
    <div className={`font-text flex flex-col ${className ?? ""}`}>
      <ol className="flex flex-col gap-125">
        {rows.map((row, index) => (
          <li
            key={row.key}
            style={{ animationDelay: `${index * 60}ms` }}
            className="motion-safe:animate-rise-in ring-fg-secondary/10 rounded-7-5x relative flex flex-col py-250 pr-250 pl-850 ring-1 ring-inset"
          >
            <span aria-hidden className={`absolute top-3.25 left-2.5 size-3.5 rounded-full ${row.lead ? "bg-brand" : "border-brand bg-surface border"}`} />
            {index < rows.length - 1 && <span aria-hidden className="bg-brand absolute top-7.25 -bottom-4 left-4.25 w-px -translate-x-1/2" />}
            <span className="text-little @3xl:text-little-lg flex items-start justify-between gap-300 leading-4.5 @3xl:leading-4.75">
              <span className="text-fg-heading min-w-0">{row.when}</span>
              <span className={`shrink-0 whitespace-nowrap ${row.lead ? "text-fg-brand" : "text-fg-heading"}`}>
                <Cents>{formatMoneyWithCode(row.amount ?? money("0", plan.total.currency), locale)}</Cents>
              </span>
            </span>
            <span className="text-little @3xl:text-little-lg text-fg-heading/50 leading-4.5 font-normal @3xl:leading-4.75">{row.detail}</span>
          </li>
        ))}
      </ol>
      <Text as="p" family="text" tone="brand" className="text-copy @3xl:text-copy-lg mt-375 text-center font-medium">
        <Cents>{t("totalLine", { amount: formatMoneyWithCode(total, locale) })}</Cents>
      </Text>
      {disclaimer && (
        <Text as="p" family="text" tone="inherit" className="text-little @3xl:text-little-lg text-fg-heading/50 text-center leading-4.5 font-normal @3xl:leading-4.75">
          {disclaimer}
        </Text>
      )}
    </div>
  )
}

type BnplTranslator = ReturnType<typeof useTranslations<"marketplace.bnpl">>

const regularRows = (plan: InstallmentView, t: BnplTranslator, locale: Locale) => {
  const dateFormat = dateFormatter(locale, { day: "numeric", month: "long", timeZone: "UTC" })
  return [
    { key: "today", when: t("today"), detail: plan.downPayment && isPositiveMoney(plan.downPayment) ? t("downPayment") : t("todayDetail"), amount: plan.downPayment, lead: true },
    ...plan.amounts.map((amount, index) => ({
      key: `d${index}`,
      when: dueLabel(plan.dueDates[index], dateFormat) ?? t("ofCount", { index: index + 1, count: plan.count }),
      detail: t("paymentOfCount", { index: index + 1, count: plan.count }),
      amount,
      lead: false,
    })),
  ]
}

// A secured plan pays the same amount every period (4 to 24 quincenas), so it collapses to today's down payment and the
// stretch until the last due date.
// Without a down payment nothing is due today (the plan header already says "Sin enganche"), so the "Hoy" row
// is dropped rather than borrowing the regular plan's credit-limit caveat.
const securedRows = (plan: InstallmentView, t: BnplTranslator, locale: Locale) => {
  const monthly = plan.paymentCadence !== "EVERY_QUINCENA"
  const hasDownPayment = plan.downPayment !== null && isPositiveMoney(plan.downPayment)
  const lastDue = plan.dueDates.at(-1)
  const until = lastDue && untilLabel(lastDue, dateFormatter(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }))
  const today = { key: "today", when: t("today"), detail: t(monthly ? "securedTodayDetailMonthly" : "securedTodayDetail"), amount: plan.downPayment, lead: true }
  return [
    ...(hasDownPayment ? [today] : []),
    {
      key: "rest",
      when: until ? t("untilDate", { date: until }) : t("paymentOfCount", { index: plan.count, count: plan.count }),
      detail: t(monthly ? "everyMonth" : "everyQuincena"),
      amount: plan.amounts[0] ?? null,
      lead: false,
    },
  ]
}

// Checkout schedule labels can arrive already formatted by the API ("20 may 2027"); those pass through as they are.
const untilLabel = (date: string, format: Intl.DateTimeFormat): string => (/^\d{4}-\d{2}-\d{2}/.test(date) ? (dueLabel(date, format) ?? date) : date)

const dueLabel = (iso: string | undefined, format: Intl.DateTimeFormat): string | null => {
  if (!iso) return null
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? null : format.format(parsed)
}
